/**
 * IndexedDB-based offline storage for PHC consultations
 * Enables true offline-first data capture for CHWs in areas without connectivity.
 * Data syncs to Supabase when connection is restored.
 */

const DB_NAME = 'phc-offline-db';
const DB_VERSION = 4;
const CONSULTATIONS_STORE = 'consultations';
const REFERRALS_STORE = 'referrals';
const VHF_CONTACTS_STORE = 'vhf_contacts';
const VHF_FOLLOWUPS_STORE = 'vhf_followups';
const LAB_RESULTS_STORE = 'lab_results';
const LAB_RESULT_UPDATES_STORE = 'lab_result_updates';

export type EncounterType =
  | 'opd' | 'chw_visit' | 'follow_up' | 'household' | 'other'
  | 'clinician_review'
  | 'campaign_screening' | 'campaign_immunization' | 'campaign_mnch'
  | 'surveillance_linelist';
export type Comorbidity =
  | 'hiv' | 'diabetes' | 'malnutrition' | 'pregnancy' | 'immunocompromised'
  | 'tb' | 'sickle_cell' | 'chronic_kidney' | 'chronic_liver' | 'asthma_copd';
export type VulnerableFlag =
  | 'under_5' | 'over_65' | 'pregnant' | 'immunocompromised' | 'malnourished' | 'disability';

export interface OfflineConsultation {
  localId: string;
  patientId: string;
  chwUserId: string;
  chiefComplaint: string;
  symptoms: string[];
  dangerSigns: string[];
  triageLevel: string | null;
  aiCdstUsed: boolean;
  aiCdstResult: any | null;
  cdstChwOverride: boolean;
  cdstOverrideReason: string | null;
  cdstFinalTriage: string | null;
  cdstTimeToDecisionSeconds: number | null;
  referralNeeded: boolean;
  referralFacility: string | null;
  treatmentGiven: string | null;
  consultationOutcome: string | null;
  followUpDate: string | null;
  followUpNotes: string | null;
  notes: string | null;
  locationLat: number | null;
  locationLng: number | null;
  // CARE-AMR Phase 1
  encounterType?: EncounterType | null;
  comorbidities?: Comorbidity[];
  vulnerableFlags?: VulnerableFlag[];
  isVulnerable?: boolean;
  householdRef?: string | null;
  createdAt: string;
  synced: boolean;
  syncedAt: string | null;
  remoteId: string | null;
}

export interface OfflineReferral {
  localId: string;
  consultationLocalId: string;
  patientId: string;
  referredBy: string;
  referralFacility: string;
  referralReason: string;
  urgency: string;
  status: string;
  createdAt: string;
  synced: boolean;
  remoteId: string | null;
}

export interface OfflineVhfContact {
  localId: string;
  outbreakId: string;
  organizationId: string;
  indexCaseId: string | null;
  parentContactId: string | null;
  contactNameEncrypted: string | null;
  contactPhoneEncrypted: string | null;
  ageYears: number | null;
  sex: string | null;
  relationshipToIndex: string | null;
  exposureDate: string;
  exposureRiskTier: 'high' | 'medium' | 'low';
  followUpStart: string;
  followUpEnd: string;
  geographicLocation: any | null;
  notes: string | null;
  createdAt: string;
  synced: boolean;
  remoteId: string | null;
}

export interface OfflineVhfFollowup {
  localId: string;
  contactLocalId: string;
  contactRemoteId: string | null;
  outbreakId: string;
  organizationId: string;
  followupDate: string;
  feverTempC: number | null;
  symptoms: string[];
  isSymptomatic: boolean;
  notes: string | null;
  capturedOfflineAt: string;
  deviceId: string | null;
  createdAt: string;
  synced: boolean;
  remoteId: string | null;
}

export interface OfflineLabResult {
  localId: string;
  patientId: string;
  phcSiteLinkId: string;
  trialSiteId: string | null;
  consultationId: string | null;
  collectedBy: string;
  collectedAt: string;
  resultedAt: string | null;
  sampleType: string;
  testName: string;
  testCode: string | null;
  resultValue: string | null;
  resultUnit: string | null;
  referenceRange: string | null;
  abnormalFlag: string | null;
  status: 'draft' | 'final' | 'amended';
  externalLabRef: string | null;
  notes: string | null;
  payload?: Record<string, unknown> | null;
  offlineSubmissionUuid: string;
  createdAt: string;
  synced: boolean;
  remoteId: string | null;
}

/**
 * Queued update for a lab result row that already exists remotely.
 * Lab technicians use this to submit values against previously-received
 * specimens even when offline. The remoteId is the phc_lab_results.id.
 */
export interface OfflineLabResultUpdate {
  remoteId: string;
  patientId: string;
  phcSiteLinkId: string;
  patch: {
    result_value?: string | null;
    result_unit?: string | null;
    reference_range?: string | null;
    abnormal_flag?: string | null;
    status?: 'draft' | 'final' | 'amended';
    resulted_at?: string | null;
    notes?: string | null;
    external_lab_ref?: string | null;
    test_code?: string | null;
    payload?: Record<string, unknown> | null;
  };
  queuedAt: string;
  synced: boolean;
}


function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(CONSULTATIONS_STORE)) {
        const store = db.createObjectStore(CONSULTATIONS_STORE, { keyPath: 'localId' });
        store.createIndex('synced', 'synced', { unique: false });
        store.createIndex('patientId', 'patientId', { unique: false });
        store.createIndex('chwUserId', 'chwUserId', { unique: false });
      }
      if (!db.objectStoreNames.contains(REFERRALS_STORE)) {
        const store = db.createObjectStore(REFERRALS_STORE, { keyPath: 'localId' });
        store.createIndex('synced', 'synced', { unique: false });
        store.createIndex('consultationLocalId', 'consultationLocalId', { unique: false });
      }
      if (!db.objectStoreNames.contains(VHF_CONTACTS_STORE)) {
        const store = db.createObjectStore(VHF_CONTACTS_STORE, { keyPath: 'localId' });
        store.createIndex('synced', 'synced', { unique: false });
        store.createIndex('outbreakId', 'outbreakId', { unique: false });
      }
      if (!db.objectStoreNames.contains(VHF_FOLLOWUPS_STORE)) {
        const store = db.createObjectStore(VHF_FOLLOWUPS_STORE, { keyPath: 'localId' });
        store.createIndex('synced', 'synced', { unique: false });
        store.createIndex('contactLocalId', 'contactLocalId', { unique: false });
        store.createIndex('outbreakId', 'outbreakId', { unique: false });
      }
      if (!db.objectStoreNames.contains(LAB_RESULTS_STORE)) {
        const store = db.createObjectStore(LAB_RESULTS_STORE, { keyPath: 'localId' });
        store.createIndex('synced', 'synced', { unique: false });
        store.createIndex('patientId', 'patientId', { unique: false });
        store.createIndex('phcSiteLinkId', 'phcSiteLinkId', { unique: false });
      }
      if (!db.objectStoreNames.contains(LAB_RESULT_UPDATES_STORE)) {
        const store = db.createObjectStore(LAB_RESULT_UPDATES_STORE, { keyPath: 'remoteId' });
        store.createIndex('synced', 'synced', { unique: false });
        store.createIndex('patientId', 'patientId', { unique: false });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function txWrite<T>(storeName: string, action: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    const req = action(store);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    tx.oncomplete = () => db.close();
  });
}

async function txRead<T>(storeName: string, action: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly');
    const store = tx.objectStore(storeName);
    const req = action(store);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    tx.oncomplete = () => db.close();
  });
}

export const phcOfflineDb = {
  /** Save a consultation locally */
  async saveConsultation(consultation: OfflineConsultation): Promise<void> {
    await txWrite(CONSULTATIONS_STORE, (store) => store.put(consultation));
  },

  /** Get all unsynced consultations */
  async getUnsyncedConsultations(): Promise<OfflineConsultation[]> {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(CONSULTATIONS_STORE, 'readonly');
      const req = tx.objectStore(CONSULTATIONS_STORE).getAll();
      req.onsuccess = () => resolve((req.result || []).filter((r: any) => !r.synced));
      req.onerror = () => reject(req.error);
      tx.oncomplete = () => db.close();
    });
  },

  /** Mark consultation as synced */
  async markSynced(localId: string, remoteId: string): Promise<void> {
    const existing = await txRead<OfflineConsultation>(CONSULTATIONS_STORE, (store) => store.get(localId));
    if (existing) {
      existing.synced = true;
      existing.syncedAt = new Date().toISOString();
      existing.remoteId = remoteId;
      await txWrite(CONSULTATIONS_STORE, (store) => store.put(existing));
    }
  },

  /** Get consultations for a patient */
  async getPatientConsultations(patientId: string): Promise<OfflineConsultation[]> {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(CONSULTATIONS_STORE, 'readonly');
      const index = tx.objectStore(CONSULTATIONS_STORE).index('patientId');
      const req = index.getAll(IDBKeyRange.only(patientId));
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
      tx.oncomplete = () => db.close();
    });
  },

  /** Get count of unsynced items */
  async getUnsyncedCount(): Promise<number> {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(CONSULTATIONS_STORE, 'readonly');
      const req = tx.objectStore(CONSULTATIONS_STORE).getAll();
      req.onsuccess = () => resolve(((req.result || []).filter((r: any) => !r.synced)).length);
      req.onerror = () => reject(req.error);
      tx.oncomplete = () => db.close();
    });
  },

  /** Save a referral locally */
  async saveReferral(referral: OfflineReferral): Promise<void> {
    await txWrite(REFERRALS_STORE, (store) => store.put(referral));
  },

  /** Get unsynced referrals */
  async getUnsyncedReferrals(): Promise<OfflineReferral[]> {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(REFERRALS_STORE, 'readonly');
      const req = tx.objectStore(REFERRALS_STORE).getAll();
      req.onsuccess = () => resolve((req.result || []).filter((r: any) => !r.synced));
      req.onerror = () => reject(req.error);
      tx.oncomplete = () => db.close();
    });
  },

  /** Mark referral as synced */
  async markReferralSynced(localId: string, remoteId: string): Promise<void> {
    const existing = await txRead<OfflineReferral>(REFERRALS_STORE, (store) => store.get(localId));
    if (existing) {
      existing.synced = true;
      existing.remoteId = remoteId;
      await txWrite(REFERRALS_STORE, (store) => store.put(existing));
    }
  },

  // ---------- VHF Contacts ----------
  async saveVhfContact(contact: OfflineVhfContact): Promise<void> {
    await txWrite(VHF_CONTACTS_STORE, (store) => store.put(contact));
  },
  async getUnsyncedVhfContacts(): Promise<OfflineVhfContact[]> {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(VHF_CONTACTS_STORE, 'readonly');
      const req = tx.objectStore(VHF_CONTACTS_STORE).getAll();
      req.onsuccess = () => resolve((req.result || []).filter((r: any) => !r.synced));
      req.onerror = () => reject(req.error);
      tx.oncomplete = () => db.close();
    });
  },
  async getVhfContactsByOutbreak(outbreakId: string): Promise<OfflineVhfContact[]> {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(VHF_CONTACTS_STORE, 'readonly');
      const idx = tx.objectStore(VHF_CONTACTS_STORE).index('outbreakId');
      const req = idx.getAll(IDBKeyRange.only(outbreakId));
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
      tx.oncomplete = () => db.close();
    });
  },
  async markVhfContactSynced(localId: string, remoteId: string): Promise<void> {
    const existing = await txRead<OfflineVhfContact>(VHF_CONTACTS_STORE, (s) => s.get(localId));
    if (existing) {
      existing.synced = true;
      existing.remoteId = remoteId;
      await txWrite(VHF_CONTACTS_STORE, (s) => s.put(existing));
      // Backfill remoteId on dependent follow-ups
      const fups = await txRead<OfflineVhfFollowup[]>(VHF_FOLLOWUPS_STORE, (s) =>
        s.index('contactLocalId').getAll(IDBKeyRange.only(localId)),
      );
      for (const f of fups ?? []) {
        if (!f.contactRemoteId) {
          f.contactRemoteId = remoteId;
          await txWrite(VHF_FOLLOWUPS_STORE, (s) => s.put(f));
        }
      }
    }
  },

  // ---------- VHF Follow-ups ----------
  async saveVhfFollowup(followup: OfflineVhfFollowup): Promise<void> {
    await txWrite(VHF_FOLLOWUPS_STORE, (store) => store.put(followup));
  },
  async getUnsyncedVhfFollowups(): Promise<OfflineVhfFollowup[]> {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(VHF_FOLLOWUPS_STORE, 'readonly');
      const req = tx.objectStore(VHF_FOLLOWUPS_STORE).getAll();
      req.onsuccess = () => resolve((req.result || []).filter((r: any) => !r.synced));
      req.onerror = () => reject(req.error);
      tx.oncomplete = () => db.close();
    });
  },
  async getVhfFollowupsByContact(contactLocalId: string): Promise<OfflineVhfFollowup[]> {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(VHF_FOLLOWUPS_STORE, 'readonly');
      const idx = tx.objectStore(VHF_FOLLOWUPS_STORE).index('contactLocalId');
      const req = idx.getAll(IDBKeyRange.only(contactLocalId));
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
      tx.oncomplete = () => db.close();
    });
  },
  async markVhfFollowupSynced(localId: string, remoteId: string): Promise<void> {
    const existing = await txRead<OfflineVhfFollowup>(VHF_FOLLOWUPS_STORE, (s) => s.get(localId));
    if (existing) {
      existing.synced = true;
      existing.remoteId = remoteId;
      await txWrite(VHF_FOLLOWUPS_STORE, (s) => s.put(existing));
    }
  },
  async getVhfUnsyncedCount(): Promise<number> {
    const contacts = await this.getUnsyncedVhfContacts();
    const fups = await this.getUnsyncedVhfFollowups();
    return contacts.length + fups.length;
  },

  // ---------- Lab results ----------
  async saveLabResult(result: OfflineLabResult): Promise<void> {
    await txWrite(LAB_RESULTS_STORE, (store) => store.put(result));
  },
  async getUnsyncedLabResults(): Promise<OfflineLabResult[]> {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(LAB_RESULTS_STORE, 'readonly');
      const req = tx.objectStore(LAB_RESULTS_STORE).getAll();
      req.onsuccess = () => resolve((req.result || []).filter((r: any) => !r.synced));
      req.onerror = () => reject(req.error);
      tx.oncomplete = () => db.close();
    });
  },
  async getUnsyncedLabResultsCount(): Promise<number> {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(LAB_RESULTS_STORE, 'readonly');
      const req = tx.objectStore(LAB_RESULTS_STORE).getAll();
      req.onsuccess = () => resolve(((req.result || []).filter((r: any) => !r.synced)).length);
      req.onerror = () => reject(req.error);
      tx.oncomplete = () => db.close();
    });
  },
  async markLabResultSynced(localId: string, remoteId: string): Promise<void> {
    const existing = await txRead<OfflineLabResult>(LAB_RESULTS_STORE, (s) => s.get(localId));
    if (existing) {
      existing.synced = true;
      existing.remoteId = remoteId;
      await txWrite(LAB_RESULTS_STORE, (s) => s.put(existing));
    }
  },

  // ---------- Lab result updates (result entry against existing specimen) ----------
  async saveLabResultUpdate(update: OfflineLabResultUpdate): Promise<void> {
    await txWrite(LAB_RESULT_UPDATES_STORE, (store) => store.put(update));
  },
  async getUnsyncedLabResultUpdates(): Promise<OfflineLabResultUpdate[]> {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(LAB_RESULT_UPDATES_STORE, 'readonly');
      const req = tx.objectStore(LAB_RESULT_UPDATES_STORE).getAll();
      req.onsuccess = () => resolve((req.result || []).filter((r: any) => !r.synced));
      req.onerror = () => reject(req.error);
      tx.oncomplete = () => db.close();
    });
  },
  async getUnsyncedLabResultUpdatesCount(): Promise<number> {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(LAB_RESULT_UPDATES_STORE, 'readonly');
      const req = tx.objectStore(LAB_RESULT_UPDATES_STORE).getAll();
      req.onsuccess = () => resolve(((req.result || []).filter((r: any) => !r.synced)).length);
      req.onerror = () => reject(req.error);
      tx.oncomplete = () => db.close();
    });
  },
  async markLabResultUpdateSynced(remoteId: string): Promise<void> {
    const existing = await txRead<OfflineLabResultUpdate>(
      LAB_RESULT_UPDATES_STORE,
      (s) => s.get(remoteId),
    );
    if (existing) {
      existing.synced = true;
      await txWrite(LAB_RESULT_UPDATES_STORE, (s) => s.put(existing));
    }
  },
  async getPendingLabResultUpdate(remoteId: string): Promise<OfflineLabResultUpdate | undefined> {
    return txRead<OfflineLabResultUpdate | undefined>(
      LAB_RESULT_UPDATES_STORE,
      (s) => s.get(remoteId),
    );
  },
};

/**
 * Sync queued VHF contacts and follow-ups to Supabase.
 * Contacts go first so follow-ups can reference real remote IDs.
 */
export async function syncVhfQueue(
  supabase: { from: (t: string) => any },
): Promise<{ contactsSynced: number; followupsSynced: number; errors: string[] }> {
  const errors: string[] = [];
  let contactsSynced = 0;
  let followupsSynced = 0;

  const contacts = await phcOfflineDb.getUnsyncedVhfContacts();
  for (const c of contacts) {
    try {
      const { data, error } = await supabase
        .from('vhf_contacts')
        .insert({
          outbreak_id: c.outbreakId,
          organization_id: c.organizationId,
          index_case_id: c.indexCaseId,
          parent_contact_id: c.parentContactId,
          contact_name_encrypted: c.contactNameEncrypted,
          contact_phone_encrypted: c.contactPhoneEncrypted,
          age_years: c.ageYears,
          sex: c.sex,
          relationship_to_index: c.relationshipToIndex,
          exposure_date: c.exposureDate,
          exposure_risk_tier: c.exposureRiskTier,
          follow_up_start: c.followUpStart,
          follow_up_end: c.followUpEnd,
          geographic_location: c.geographicLocation ?? {},
          notes: c.notes,
        })
        .select('id')
        .single();
      if (error) throw error;
      await phcOfflineDb.markVhfContactSynced(c.localId, data.id);
      contactsSynced++;
    } catch (e: any) {
      errors.push(`contact:${c.localId}:${e?.message ?? e}`);
    }
  }

  const followups = await phcOfflineDb.getUnsyncedVhfFollowups();
  for (const f of followups) {
    if (!f.contactRemoteId) {
      // Contact hasn't synced yet; defer
      continue;
    }
    try {
      const { data, error } = await supabase
        .from('vhf_contact_followups')
        .upsert(
          {
            contact_id: f.contactRemoteId,
            outbreak_id: f.outbreakId,
            organization_id: f.organizationId,
            followup_date: f.followupDate,
            fever_temp_c: f.feverTempC,
            symptoms: f.symptoms,
            is_symptomatic: f.isSymptomatic,
            notes: f.notes,
            captured_offline_at: f.capturedOfflineAt,
            synced_at: new Date().toISOString(),
            device_id: f.deviceId,
          },
          { onConflict: 'contact_id,followup_date' },
        )
        .select('id')
        .single();
      if (error) throw error;
      await phcOfflineDb.markVhfFollowupSynced(f.localId, data.id);
      followupsSynced++;
    } catch (e: any) {
      errors.push(`followup:${f.localId}:${e?.message ?? e}`);
    }
  }

  return { contactsSynced, followupsSynced, errors };
}
